'use client';

import Link from 'next/link';

import { HeaderWrapper } from '@/components/Header/HeaderWrapper';
import { HeaderLogo } from '@/components/Header/HeaderLogo';
import { HeaderSearch } from '@/components/Header/HeaderSearch';
import { HamburgerMenu } from '@/components/Header/HamburgerMenu';
import LanguageMenuItem from '@/components/Header/LanguageMenuItem';
import MegaMenu from '@/components/Header/MegaMenu';
import { normalizeMegaMenu } from '@/features/contentful/helpers/menu';

import {
    TypeMegaMenuComponentWithoutUnresolvableLinksResponse,
    TypeSettingsWithoutUnresolvableLinksResponse,
} from '@/features/contentful/_generated';
import { TEntriesResponse } from '@/features/contentful/types/types';
import { useRouter } from 'next/navigation';
import { useCallback, useState } from 'react';
import Icon from '@/components/Icon/Icon';
import createSettingsObject from '@/utils/createSettingsObject';
import { getLabel } from '@/utils/getLabel';
import { getLocalizedLink } from '@/utils/getLocalizedLink';

const AppHeader = ({
    lang,
    megaMenu,
}: {
    lang: string;
    megaMenu: TEntriesResponse<TypeMegaMenuComponentWithoutUnresolvableLinksResponse[]>;
}) => {
    const router = useRouter();

    const { headerId, headerLinks } = normalizeMegaMenu(lang, megaMenu);
    const headerLogoLink = getLocalizedLink('/', lang);

    const handleSearch = (val: string) => {
        if (!val || !val.trim()) {
            return;
        }
        const searchParams = new URLSearchParams({ search: val, page: '1' });

        router.push(getLocalizedLink(`/search?${searchParams.toString()}`, lang));
        window.dataLayer && window.dataLayer.push({ event: 'search_pageview', search_input: val });
    };

    const handleAccessebilityOpen = useCallback(() => {
        // @ts-ignore
        if (window['loadService']) {
            // @ts-ignore
            window['loadService']();
            window.dataLayer &&
                window.dataLayer.push({ event: 'cta_click', cta_type: 'accessibility' });
            window.dataLayer && window.dataLayer.push({ event: 'accessibility_click' });
        }
    }, []);

    const menuSettings = createSettingsObject(
        megaMenu.items[0].fields.settings as TypeSettingsWithoutUnresolvableLinksResponse[],
    );

    const contactUsLabel = getLabel('label', 'contactUs', menuSettings);
    const searchLabel = getLabel('label', 'search', menuSettings);
    const accessibilityLabel = getLabel('label', 'accessibility', menuSettings);
    const chooseLanguageLabel = getLabel('label', 'chooseLanguage', menuSettings);
    const backLabel = getLabel('label', 'back', menuSettings);

    const [isHamburgerOpen, setIsHamburgerOpen] = useState(false);

    const handleHamburgerToggle = () => {
        setIsHamburgerOpen((prev) => !prev);
    };
    return (
        <HeaderWrapper>
            <div className="tw-hidden lg:tw-flex tw-flex-row tw-items-center tw-justify-between">
                <div className="tw-flex tw-flex-row tw-items-center">
                    <HeaderLogo className="tw-cursor-pointer tw-mr-[20px]" link={headerLogoLink} />
                    <MegaMenu links={headerLinks} headerId={headerId} />
                </div>
                <div className="tw-flex tw-flex-row tw-items-center tw-h-[21px]">
                    <HeaderSearch
                        className="tw-mr-4"
                        handleSearch={handleSearch}
                        searchLabel={searchLabel}
                    />
                    {/* Will be uncommented when it will work*/}
                    {/*<div*/}
                    {/*    className="xl:tw-hidden tw-cursor-pointer  tw-hidden"*/}
                    {/*    onClick={handleAccessebilityOpen}*/}
                    {/*>*/}
                    {/*    <Icon type="a11y" className="tw-mr-3 tw-mt-[5px] tw-cursor-pointer" />*/}
                    {/*</div>*/}
                    {/* Will be uncommented when it will work*/}
                    {/*<div*/}
                    {/*    onClick={handleAccessebilityOpen}*/}
                    {/*    className="*/}
                    {/*      tw-cursor-pointer*/}
                    {/*      tw-mr-[12px]*/}
                    {/*      hover:tw-no-underline*/}
                    {/*      tw-hidden*/}
                    {/*      xl:tw-flex*/}
                    {/*      xl:tw-hidden*/}
                    {/*      tw-items-center*/}
                    {/*      tw-group*/}
                    {/*      tw-rounded-[15px]*/}
                    {/*      tw-bg-[#4b4f540d]*/}
                    {/*      tw-px-[8px]*/}
                    {/*      tw-h-[34px]*/}
                    {/*      tw-w-[120px]*/}
                    {/*      "*/}
                    {/*>*/}
                    {/*    <Icon*/}
                    {/*        type="a11y"*/}
                    {/*        className="tw-mr-1 tw-cursor-pointer tw-w-[16px] tw-h-[15px]"*/}
                    {/*    />*/}
                    {/*    <span*/}
                    {/*        className="*/}
                    {/*     tw-text-[13px]*/}
                    {/*     tw-text-menuText*/}
                    {/*     tw-font-gotham-medium*/}
                    {/*     group-hover:tw-text-pink1*/}
                    {/*     group-hover:tw-no-underline*/}
                    {/*     tw-transition-colors*/}
                    {/*     tw-duration-300*/}
                    {/*     "*/}
                    {/*    >*/}
                    {/*        {accessibilityLabel}*/}
                    {/*    </span>*/}
                    {/*</div>*/}
                    <LanguageMenuItem />
                    <Link
                        className="tw-bg-pink1 tw-rounded-[0.25em] tw-px-[7px] tw-pt-3 tw-pb-[10px] tw-text-white hover:tw-text-white hover:tw-no-underline tw-transition-all hover:tw-shadow-menu hover:-tw-translate-y-[1px]"
                        href={getLocalizedLink('contact-us', lang)}
                    >
                        {contactUsLabel}
                    </Link>
                </div>
            </div>
            <div className="tw-flex tw-flex-row tw-justify-between tw-items-center lg:tw-hidden">
                <div>
                    <HeaderLogo
                        className="tw-cursor-pointer"
                        link={headerLogoLink}
                        closeHamburger={() => setIsHamburgerOpen(false)}
                    />
                </div>
                <div className="tw-flex tw-flex-row">
                    <HeaderSearch
                        className="tw-mr-4"
                        handleSearch={handleSearch}
                        searchLabel={searchLabel}
                    />
                    <HamburgerMenu
                        links={headerLinks}
                        lang={lang}
                        labels={{
                            contactUs: contactUsLabel,
                            accessibility: accessibilityLabel,
                            chooseLanguage: chooseLanguageLabel,
                            back: backLabel,
                        }}
                        isHamburgerOpen={isHamburgerOpen}
                        setIsHamburgerOpen={setIsHamburgerOpen}
                    />
                </div>
            </div>
        </HeaderWrapper>
    );
};

export default AppHeader;
